import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Phone, MessageCircle, Clock, Shield, Palette, Waves, ChevronDown, Award, Star, Heart, Quote } from 'lucide-react';
import { brandTheme } from '../styles/BrandTheme';
import HeroSectionComponent from '../components/HeroSection';

const HeroSection = styled.section`
  height: 100vh;
  background: 
    linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 100%),
    url('/images/resin-surfaces-bound-work9.jpeg') center/cover;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;
  padding: 0 1rem;

  /* iPad Pro and Large Tablets */
  @media (max-width: 1366px) and (min-width: 1025px) {
    height: 100vh;
    padding: 0 2rem;
  }

  /* iPad Air and Medium Tablets */
  @media (max-width: 1024px) and (min-width: 769px) {
    height: 100vh;
    padding: 0 1.5rem;
  }

  /* Landscape Mobile and Small Tablets */
  @media (max-width: 768px) and (min-width: 569px) and (orientation: landscape) {
    height: 100vh;
    padding: 0 1.25rem;
  }

  /* Portrait Mobile - iPhone 12 Pro Max and Large Mobile */
  @media (max-width: 768px) and (min-width: 428px) and (orientation: portrait) {
    height: 100vh;
    padding: 0 1.25rem;
  }

  /* iPhone 12 and Standard Mobile */
  @media (max-width: 427px) and (min-width: 390px) {
    height: 100vh;
    padding: 0 1rem;
  }

  /* iPhone SE and Small Mobile */
  @media (max-width: 389px) and (min-width: 320px) {
    height: 100vh;
    padding: 0 0.75rem;
  }

  /* Landscape orientation adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    height: 100vh;
    padding: 0 1rem;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, transparent 30%, rgba(254, 206, 27, 0.1) 50%, transparent 70%);
    animation: shimmer 3s ease-in-out infinite;
  }

  @keyframes shimmer {
    0%, 100% { transform: translateX(-100%); }
    50% { transform: translateX(100%); }
  }
`;

const HeroContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 1rem;
  position: relative;
  z-index: 2;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    padding: 0 0.5rem;
    max-width: 100%;
  }

  @media (max-width: 389px) {
    padding: 0 0.25rem;
  }
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(2rem, 5.5vw, 4.5rem);
  margin-bottom: 1.5rem;
  font-weight: 700;
  line-height: 1.1;
  background: linear-gradient(135deg, #FECE1B 0%, #ffffff 50%, #FECE1B 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);

  /* Mobile adjustments */
  @media (max-width: 389px) {
    font-size: clamp(1.75rem, 5vw, 2.5rem);
    margin-bottom: 1rem;
    line-height: 1.2;
  }

  @media (max-width: 427px) and (min-width: 390px) {
    font-size: clamp(1.875rem, 5.25vw, 3rem);
    margin-bottom: 1.25rem;
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    font-size: clamp(1.5rem, 4vw, 2.5rem);
    margin-bottom: 0.75rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: clamp(1rem, 2.5vw, 1.5rem);
  margin-bottom: 2.5rem;
  opacity: 0.95;
  line-height: 1.6;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);

  /* Mobile adjustments */
  @media (max-width: 389px) {
    font-size: clamp(0.875rem, 2.25vw, 1.125rem);
    margin-bottom: 1.5rem;
    line-height: 1.5;
    max-width: 100%;
  }

  @media (max-width: 427px) and (min-width: 390px) {
    font-size: clamp(0.9375rem, 2.375vw, 1.25rem);
    margin-bottom: 2rem;
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    font-size: clamp(0.75rem, 2vw, 1rem);
    margin-bottom: 1rem;
    line-height: 1.4;
  }
`;

const CTAButtons = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 3rem;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    margin-bottom: 2rem;
  }

  @media (max-width: 389px) {
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    flex-direction: row;
    gap: 1rem;
    margin-bottom: 1rem;
  }
`;

const HeroBadge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(254, 206, 27, 0.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50px;
  padding: 0.75rem 1.5rem;
  margin-bottom: 2rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: white;

  svg {
    color: #FECE1B;
  }

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    margin-bottom: 1.5rem;
    font-size: 0.8rem;
  }

  @media (max-width: 389px) {
    padding: 0.4rem 0.75rem;
    margin-bottom: 1rem;
    font-size: 0.75rem;
    gap: 0.25rem;
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    padding: 0.3rem 0.75rem;
    margin-bottom: 0.75rem;
    font-size: 0.7rem;
  }
`;

const StyledButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 2.5rem;
  border-radius: 50px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 1.1rem;
  position: relative;
  overflow: hidden;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    padding: 0.875rem 1.75rem;
    font-size: 0.95rem;
    gap: 0.5rem;
  }

  @media (max-width: 427px) and (min-width: 390px) {
    padding: 0.75rem 1.5rem;
    font-size: 0.9rem;
  }

  @media (max-width: 389px) {
    padding: 0.625rem 1.25rem;
    font-size: 0.85rem;
    gap: 0.375rem;
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
    gap: 0.25rem;
  }

  /* Icon sizing for mobile */
  @media (max-width: 768px) {
    svg {
      width: 16px;
      height: 16px;
    }
  }

  @media (max-width: 389px) {
    svg {
      width: 14px;
      height: 14px;
    }
  }

  @media (orientation: landscape) and (max-height: 500px) {
    svg {
      width: 12px;
      height: 12px;
    }
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  &.primary {
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    color: white;
    box-shadow: 0 10px 30px rgba(254, 206, 27, 0.4);
    border: 2px solid transparent;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 15px 40px rgba(254, 206, 27, 0.5);
    }
  }

  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(10px);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
      border-color: rgba(255, 255, 255, 0.5);
      transform: translateY(-2px);
    }
  }

  &.quote-button {
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    color: white;
    box-shadow: 
      0 8px 32px rgba(254, 206, 27, 0.4),
      0 0 0 1px rgba(255, 255, 255, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);
    border: none;
    position: relative;
    overflow: hidden;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    font-size: 1.1rem;
    border-radius: 50px;
    backdrop-filter: blur(10px);

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
      transition: left 0.6s ease;
      border-radius: 50px;
    }

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, transparent 50%, rgba(255, 255, 255, 0.05) 100%);
      opacity: 0;
      transition: opacity 0.3s ease;
      border-radius: 50px;
    }

    &:hover {
      transform: translateY(-3px) scale(1.03);
      box-shadow: 
        0 12px 40px rgba(254, 206, 27, 0.6),
        0 0 0 1px rgba(255, 255, 255, 0.15),
        inset 0 1px 0 rgba(255, 255, 255, 0.15);
    }

    &:hover::before {
      left: 100%;
    }

    &:hover::after {
      opacity: 1;
    }

    &:active {
      transform: translateY(-2px) scale(1.02);
    }

    svg {
      transition: transform 0.3s ease;
      z-index: 2;
      position: relative;
    }

    &:hover svg {
      transform: translateX(3px);
    }

    @keyframes quotePulse {
      0%, 100% { 
        box-shadow: 0 25px 60px rgba(254, 206, 27, 0.7), 0 0 0 2px rgba(255, 255, 255, 0.3);
      }
      50% { 
        box-shadow: 0 30px 70px rgba(254, 206, 27, 0.9), 0 0 0 3px rgba(255, 255, 255, 0.4);
      }
    }

    /* Mobile adjustments for quote button */
    @media (max-width: 768px) {
      font-size: 1rem;
      letter-spacing: 0.3px;
    }

    @media (max-width: 389px) {
      font-size: 0.9rem;
      letter-spacing: 0.2px;
    }
  }
`;

const ScrollIndicator = styled(motion.div)`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: #FECE1B;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  padding: 0.75rem 1rem;
  border-radius: 25px;
  border: 1px solid rgba(254, 206, 27, 0.3);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);

  &:hover {
    color: #FECE1B;
    transform: translateX(-50%) translateY(-5px);
    background: rgba(254, 206, 27, 0.1);
    box-shadow: 0 12px 35px rgba(254, 206, 27, 0.2);
  }

  span {
    font-size: 0.85rem;
    font-weight: 600;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }

  svg {
    filter: drop-shadow(0 0 8px rgba(254, 206, 27, 0.5));
    animation: bounce 2s infinite;
  }

  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
    40% { transform: translateY(-10px); }
    60% { transform: translateY(-5px); }
  }

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    bottom: 1.5rem;
    padding: 0.4rem 0.6rem;
    gap: 0.25rem;
    
    span {
      font-size: 0.75rem;
    }
    
    svg {
      width: 16px;
      height: 16px;
    }
  }

  @media (max-width: 427px) and (min-width: 390px) {
    bottom: 1.25rem;
    padding: 0.35rem 0.5rem;
    
    span {
      font-size: 0.7rem;
    }
    
    svg {
      width: 14px;
      height: 14px;
    }
  }

  @media (max-width: 389px) {
    bottom: 1rem;
    padding: 0.3rem 0.45rem;
    gap: 0.2rem;
    
    span {
      font-size: 0.65rem;
    }
    
    svg {
      width: 12px;
      height: 12px;
    }
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    bottom: 0.75rem;
    padding: 0.25rem 0.4rem;
    gap: 0.15rem;
    
    span {
      font-size: 0.6rem;
    }
    
    svg {
      width: 10px;
      height: 10px;
    }
  }
`;

const Section = styled.section`
  padding: 6rem 0;
  position: relative;

  &:nth-child(even) {
    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  }

  &:nth-child(odd) {
    background: white;
  }

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  position: relative;
  z-index: 2;
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: 1rem;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 700;
  color: ${brandTheme.colors.primary[900]};
  line-height: 1.2;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -1rem;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(135deg, ${brandTheme.colors.primary[500]} 0%, ${brandTheme.colors.secondary[500]} 100%);
    border-radius: 2px;
  }
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  img {
    width: 100%;
    height: 400px;
    object-fit: cover;
    border-radius: 15px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const FeatureCard = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  text-align: center;
  border: 1px solid #e5e7eb;

  .icon {
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1rem;
    color: white;
  }

  h3 {
    margin-bottom: 1rem;
    color: #1a1a1a;
  }

  p {
    color: #666;
  }
`;

const BenefitsList = styled.div`
  ul {
    list-style: none;
    padding: 0;
  }

  li {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
    padding: 1rem;
    background: #f8fafc;
    border-radius: 10px;
    border-left: 4px solid #FECE1B;

    svg {
      color: #10b981;
      flex-shrink: 0;
    }
  }
`;

const SafetySection = styled.section`
  background: #f8fafc;
  padding: 5rem 0;
`;

const SafetyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`;

const SafetyCard = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;

  h3 {
    color: #FECE1B;
    margin-bottom: 1rem;
  }

  ul {
    list-style: none;
    padding: 0;

    li {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 0.5rem;
      color: #555;

      svg {
        color: #10b981;
        flex-shrink: 0;
      }
    }
  }
`;

const ProcessSection = styled.section`
  background: white;
  padding: 5rem 0;
`;

const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
`;

const ProcessStep = styled(motion.div)`
  text-align: center;
  padding: 1.5rem;

  .step-number {
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 1.2rem;
    margin: 0 auto 1rem;
  }

  h3 {
    margin-bottom: 0.5rem;
    color: #1a1a1a;
  }

  p {
    color: #666;
    font-size: 0.9rem;
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  font-size: clamp(1rem, 2.5vw, 1.25rem);
  color: ${brandTheme.colors.neutral.gray[600]};
  max-width: 600px;
  margin: 0 auto 3rem auto;
  line-height: 1.6;
`;

const FAQSection = styled.section`
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 6rem 0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23e2e8f0' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E") repeat;
    opacity: 0.3;
  }

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const FAQGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  max-width: 800px;
  margin: 0 auto;
`;

const FAQItem = styled(motion.div)`
  background: white;
  border-radius: 15px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
  }

  .question {
    padding: 1.5rem 2rem;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: 600;
    font-size: 1.1rem;
    color: ${brandTheme.colors.primary[900]};
    transition: all 0.3s ease;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);

    &:hover {
      background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
    }

    svg {
      transition: transform 0.3s ease;
      color: ${brandTheme.colors.primary[500]};
    }

    &.active svg {
      transform: rotate(180deg);
    }
  }

  .answer {
    padding: 0 2rem;
    max-height: 0;
    overflow: hidden;
    transition: all 0.3s ease;
    background: white;
    color: ${brandTheme.colors.neutral.gray[700]};
    line-height: 1.6;

    &.active {
      padding: 0 2rem 1.5rem 2rem;
      max-height: 200px;
    }
  }

  @media (max-width: 768px) {
    .question {
      padding: 1.25rem 1.5rem;
      font-size: 1rem;
    }

    .answer {
      &.active {
        padding: 0 1.5rem 1.25rem 1.5rem;
      }
    }
  }
`;

const CTASection = styled.section`
  background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
  color: white;
  padding: 6rem 0;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E") repeat;
  }

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const PoolDecksPage: React.FC = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const faqs = [
    {
      question: 'How long does a resin pool deck installation take?',
      answer: 'Most residential resin pool decks are completed within 1-2 days. The exact timeframe depends on the size of the pool area, any existing surface preparation needed, and weather conditions. We provide a detailed timeline during your free assessment.'
    },
    {
      question: 'Are resin pool decks safe around pools?',
      answer: 'Yes, our resin bound pool decks are specifically designed for pool environments. They feature anti-slip textured finishes, are resistant to pool chemicals and chlorine, and provide excellent drainage to prevent water pooling around your pool.'
    },
    {
      question: 'Can resin pool decks be installed around existing pools?',
      answer: 'Absolutely! We can install resin bound pool decks around both new and existing pools. Our installation process is designed to work around your pool without causing damage, and we take extra care to protect your pool during the installation process.'
    },
    {
      question: 'What maintenance do resin pool decks require?',
      answer: 'Resin pool decks are very low maintenance. Regular cleaning with soap and water is usually sufficient. The surface is resistant to staining from pool chemicals and doesn\'t require sealing. We recommend annual inspection to maintain optimal performance.'
    },
    {
      question: 'How much does a resin pool deck cost?',
      answer: 'Resin pool deck costs typically range from £50-70 per square metre, depending on the size, complexity, and chosen materials. Pool-specific features like drainage channels and anti-slip finishes may affect pricing. We provide free, detailed quotes for all projects.'
    },
    {
      question: 'Can resin pool decks be customised?',
      answer: 'Yes! We offer a wide range of colours and can create custom patterns or designs to complement your pool and outdoor space. We can also incorporate drainage channels, borders, and other features to enhance both safety and aesthetics.'
    }
  ];

  const features = [
    {
      icon: <Waves size={30} />,
      title: 'Pool-Friendly',
      description: 'Safe for pool water and chemicals, won\'t degrade over time'
    },
    {
      icon: <Shield size={30} />,
      title: 'Anti-Slip Finish',
      description: 'Special texture provides grip even when wet'
    },
    {
      icon: <Clock size={30} />,
      title: 'Quick Installation',
      description: 'Pool decks completed in 1-2 days with minimal disruption'
    },
    {
      icon: <Palette size={30} />,
      title: 'Custom Designs',
      description: 'Create stunning pool surrounds that complement your pool'
    }
  ];

  const benefits = [
    'Anti-slip surface for safety around pools',
    'Resistant to pool chemicals and chlorine',
    'Quick-drying surface after splashing',
    'Easy to clean and maintain',
    'No weeds or moss growth',
    'Customisable colours and patterns',
    'Professional installation guaranteed',
    'Suitable for all pool types'
  ];

  const safetyFeatures = [
    {
      title: 'Safety Features',
      features: [
        'Anti-slip textured finish',
        'Drainage channels for water runoff',
        'Smooth edges to prevent trips',
        'Non-porous surface'
      ]
    },
    {
      title: 'Durability',
      features: [
        'UV resistant materials',
        'Chemical resistant finish',
        'Weather resistant coating',
        'Long-lasting performance'
      ]
    },
    {
      title: 'Maintenance',
      features: [
        'Easy to clean with soap and water',
        'No sealing required',
        'Resistant to staining',
        'Minimal ongoing maintenance'
      ]
    }
  ];

  const processSteps = [
    {
      number: 1,
      title: 'Pool Assessment',
      description: 'Evaluate your pool area and discuss design preferences'
    },
    {
      number: 2,
      title: 'Surface Preparation',
      description: 'Prepare the area around your pool for resin application'
    },
    {
      number: 3,
      title: 'Resin Application',
      description: 'Apply anti-slip resin bound mixture with your chosen colour'
    },
    {
      number: 4,
      title: 'Final Inspection',
      description: 'Quality check and provide care instructions for your pool deck'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Resin Pool Decks London | Premium Anti-Slip Pool Surround Installation</title>
        <meta name="description" content="Expert resin bound pool deck installation in London, Surrey & Hertfordshire. Anti-slip, pool-friendly, quick-drying surface. Free quotes, professional installation. Safe pool surrounds today!" />
        <meta name="keywords" content="resin pool decks London, pool surrounds London, anti-slip pool deck London, resin pool decking London, pool deck installation London, resin pool decks Westminster, pool surrounds Camden, resin pool decks Islington, pool surrounds Hackney, resin pool decks Tower Hamlets, pool surrounds Greenwich, resin pool decks Lewisham, pool surrounds Southwark, resin pool decks Lambeth, pool surrounds Wandsworth, resin pool decks Hammersmith, pool surrounds Kensington, resin pool decks Chelsea, pool surrounds Fulham, resin pool decks Richmond, pool surrounds Kingston, resin pool decks Merton, pool surrounds Sutton, resin pool decks Croydon, pool surrounds Bromley, resin pool decks Bexley, pool surrounds Havering, resin pool decks Redbridge, pool surrounds Waltham Forest, resin pool decks Enfield, pool surrounds Barnet, resin pool decks Harrow, pool surrounds Hillingdon, resin pool decks Ealing, pool surrounds Hounslow, pool surrounds Brent, resin pool decks Haringey, pool surrounds Newham, resin pool decks Barking, pool surrounds Dagenham, anti-slip pool deck London, resin pool decking London, pool deck contractors London, pool deck specialists London, pool deck design London, pool deck quotes London, best pool deck company London, pool deck near me London, resin pool deck installation London, pool deck maintenance London" />
        <link rel="canonical" href="https://www.resinsurfaceslondon.co.uk/resin-pool-decks-london/" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Resin Bound Pool Deck Installation",
            "description": "Premium anti-slip resin bound pool deck installation service in London",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Resin Surfaces London",
              "url": "https://www.resinsurfaceslondon.co.uk"
            },
            "areaServed": ["London", "Surrey", "Hertfordshire"],
            "serviceType": "Resin Bound Pool Deck Installation"
          })}
        </script>
      </Helmet>

      <HeroSectionComponent
        title="Premium Resin Pool Decks London"
        subtitle="Create safe, beautiful pool surrounds with our anti-slip resin bound surfacing. Pool-friendly, chemical-resistant, and quick-drying."
        badge="London's #1 Resin Pool Deck Specialists"
        backgroundImage="/images/resin-surfaces-bound-work9.jpeg"
        primaryButtonText="Get Free Quote"
        primaryButtonLink="/contact/"
        secondaryButtonText="Call Now"
        secondaryButtonLink="tel:+447535935779"
      />

      <Section>
        <Container>
          <TwoColumnGrid>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <img src="/images/resin-surfaces-bound-work9.jpeg" alt="Resin bound pool deck London" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2>Safe, Beautiful Pool Surrounds</h2>
              <p>
                Resin bound pool decks provide the perfect solution for safe, attractive pool surrounds. 
                Our anti-slip surfacing ensures safety around your pool while maintaining the aesthetic 
                appeal of your outdoor space.
              </p>
              <p>
                Designed specifically for pool environments, our resin bound pool decks are resistant 
                to pool chemicals, quick-drying, and easy to maintain. Whether you have an above-ground 
                or in-ground pool, we can create the perfect surround.
              </p>
              <BenefitsList>
                <ul>
                  {benefits.slice(0, 4).map((benefit, index) => (
                    <li key={index}>
                      <CheckCircle size={20} />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </BenefitsList>
            </motion.div>
          </TwoColumnGrid>

          <SectionTitle>Key Features & Benefits</SectionTitle>
          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </FeatureCard>
            ))}
          </FeaturesGrid>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2>Complete Benefits List</h2>
            <BenefitsList>
              <ul>
                {benefits.map((benefit, index) => (
                  <li key={index}>
                    <CheckCircle size={20} />
                    {benefit}
                  </li>
                ))}
              </ul>
            </BenefitsList>
          </motion.div>
        </Container>
      </Section>

      <SafetySection>
        <Container>
          <SectionTitle>Safety & Performance Features</SectionTitle>
          <SafetyGrid>
            {safetyFeatures.map((feature, index) => (
              <SafetyCard
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <h3>{feature.title}</h3>
                <ul>
                  {feature.features.map((item, i) => (
                    <li key={i}>
                      <CheckCircle size={16} />
                      {item}
                    </li>
                  ))}
                </ul>
              </SafetyCard>
            ))}
          </SafetyGrid>
        </Container>
      </SafetySection>

      <ProcessSection>
        <Container>
          <SectionTitle>Our Installation Process</SectionTitle>
          <ProcessGrid>
            {processSteps.map((step, index) => (
              <ProcessStep
                key={step.number}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="step-number">{step.number}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </ProcessStep>
            ))}
          </ProcessGrid>
        </Container>
      </ProcessSection>

      <FAQSection>
        <Container>
          <SectionTitle>Frequently Asked Questions About Resin Pool Decks</SectionTitle>
          <SectionSubtitle>
            Get answers to common questions about our resin pool deck installation, safety features, and maintenance
          </SectionSubtitle>
          <FAQGrid>
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                className={openFAQ === index ? 'active' : ''}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div 
                  className={`question ${openFAQ === index ? 'active' : ''}`}
                  onClick={() => toggleFAQ(index)}
                >
                  {faq.question}
                  <ChevronDown size={20} />
                </div>
                <div className={`answer ${openFAQ === index ? 'active' : ''}`}>
                  {faq.answer}
                </div>
              </FAQItem>
            ))}
          </FAQGrid>
        </Container>
      </FAQSection>

      <CTASection>
        <Container>
          <SectionTitle style={{ color: 'white' }}>Ready for Your Pool Deck?</SectionTitle>
          <p style={{ fontSize: '1.2rem', marginBottom: '2rem', opacity: 0.9 }}>
            Create a safe, beautiful pool surround that enhances your swimming experience
          </p>
          <CTAButtons>
            <StyledButton to="/contact/" className="quote-button">
              Get Free Quote <ArrowRight size={20} />
            </StyledButton>
            <StyledButton to="https://wa.me/447535935779" className="secondary">
              <MessageCircle size={20} /> WhatsApp Chat
            </StyledButton>
          </CTAButtons>
        </Container>
      </CTASection>
    </>
  );
};

export default PoolDecksPage;
